/**
 * RecommendationText — renders the server-computed differential-diagnosis
 * recommendation attached to every spine Source (Phase 1 PR 17).
 *
 * Two variants:
 *
 *   compact — inline one-liner shown on /sources rows. The headline +
 *     a severity dot. Designed to read at scan speed when 20+ rows are
 *     visible.
 *
 *   expanded — block layout shown on /sources/:id Overview. Headline +
 *     full detail copy. Replaces the pre-PR-17 hand-written
 *     RecommendationPlaceholder.
 *
 * Both variants suppress kind=`none` recommendations (defensive default
 * in the matrix; no surface area).
 *
 * Severity colour vocabulary matches the spine page badges (rose =
 * critical/high, amber = medium, slate = low/info, emerald = resolved-style).
 * Action chip integration (e.g. inline "Classify" button when kind=classify)
 * lives on the page side because it depends on each surface's auth /
 * permission state.
 */

import {
  AlertTriangle,
  Sparkles,
  CheckCircle2,
  Repeat,
  HelpCircle,
} from "lucide-react";
import type { SpineRecommendation } from "@/lib/api";

type Variant = "compact" | "expanded";

interface RecommendationTextProps {
  recommendation: SpineRecommendation;
  variant?: Variant;
}

export function RecommendationText({
  recommendation,
  variant = "compact",
}: RecommendationTextProps) {
  if (recommendation.kind === "none") return null;

  const palette = severityPalette(recommendation.severity);
  const Icon = iconForKind(recommendation.kind);

  if (variant === "compact") {
    return (
      <div
        className={`inline-flex items-start gap-1.5 text-xs ${palette.text}`}
        role="note"
        aria-label={`Recommendation: ${recommendation.headline}`}
      >
        <Icon className="w-3.5 h-3.5 flex-shrink-0 mt-px" aria-hidden="true" />
        <span className="font-medium">{recommendation.headline}</span>
      </div>
    );
  }

  // Expanded: full headline + detail in a coloured block. Used on
  // the source-detail Overview tab where there's room to breathe.
  return (
    <div
      className={`flex items-start gap-3 p-3 rounded-md border ${palette.border} ${palette.bg}`}
      role="note"
      aria-label={`Recommendation: ${recommendation.headline}`}
    >
      <Icon className={`w-5 h-5 flex-shrink-0 mt-0.5 ${palette.icon}`} aria-hidden="true" />
      <div className="space-y-1 min-w-0">
        <div className={`text-sm font-semibold ${palette.text}`}>
          {recommendation.headline}
        </div>
        <p className={`text-sm ${palette.detailText}`}>{recommendation.detail}</p>
      </div>
    </div>
  );
}

interface Palette {
  text: string;
  detailText: string;
  bg: string;
  border: string;
  icon: string;
}

function severityPalette(
  severity: SpineRecommendation["severity"]
): Palette {
  switch (severity) {
    case "critical":
      return {
        text: "text-rose-900",
        detailText: "text-rose-800",
        bg: "bg-rose-50",
        border: "border-rose-300",
        icon: "text-rose-600",
      };
    case "high":
      return {
        text: "text-rose-800",
        detailText: "text-rose-700",
        bg: "bg-rose-50",
        border: "border-rose-200",
        icon: "text-rose-500",
      };
    case "medium":
      return {
        text: "text-amber-900",
        detailText: "text-amber-800",
        bg: "bg-amber-50",
        border: "border-amber-200",
        icon: "text-amber-600",
      };
    case "low":
      return {
        text: "text-slate-700",
        detailText: "text-slate-600",
        bg: "bg-slate-50",
        border: "border-slate-200",
        icon: "text-slate-500",
      };
    case "info":
    default:
      return {
        text: "text-emerald-800",
        detailText: "text-slate-600",
        bg: "bg-emerald-50",
        border: "border-emerald-200",
        icon: "text-emerald-600",
      };
  }
}

function iconForKind(kind: SpineRecommendation["kind"]) {
  switch (kind) {
    case "fix_alignment":
      return AlertTriangle;
    case "classify":
      return Sparkles;
    case "matcher_review":
      return HelpCircle;
    case "forwarder_expected":
      return Repeat;
    case "resolved":
      return CheckCircle2;
    case "awaiting_data":
    case "none":
    default:
      return HelpCircle;
  }
}
